/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */
/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'

import { currencyNumberFormat, translate } from 'utils/utility'
// import BookingSellerReservationDetailUpsellQuantity
//     from 'pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerReservationDetailUpsellQuantity'
import { useTranslation } from 'react-i18next'
import { PackagesCartItem } from 'types/entities/PackagesCartItem'
import { Reservation } from 'types/entities/Reservation'
import { PackageItemPersonType } from 'types/enums/PackageItemPersonType'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { TrashIcon } from '@heroicons/react/24/outline'
import { useAppDispatch } from 'helpers/store'
import SellerAction from 'store/seller/SellerAction'
import BookingLoginAction from 'store/bookingLogin/BookingLoginAction'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import { closeSecondaryModal, openSecondaryModal } from 'helpers/actions'
import { ModalSize } from 'types/enums/ModalSize'
import { toast } from 'utils/toastUtils'
import clsx from 'clsx'
import { UiTableCell, UiTableRow } from 'components/Ui/UiTable'
import { PackageItemItemType } from 'types/enums/PackageItemItemType'

const SellerPackageCartItemRow = (props: {
    packagesCartItem: PackagesCartItem
    reservation: Reservation
    isAccessTicket?: boolean
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [isOpen, setIsOpen] = React.useState<boolean>(false)

    const quantity =
        (props.packagesCartItem.drivers_count_adult || 0) +
        (props.packagesCartItem.drivers_count_child || 0)
    const basePrice = quantity > 0
        ? props.packagesCartItem.price / quantity
        : props.packagesCartItem.price
    const discount = props.packagesCartItem.packageItem?.original_price
        ? 100 - (basePrice / props.packagesCartItem.packageItem.original_price) * 100
        : 0

    const itemName = props.packagesCartItem.article_shortname
        ? translate(props.packagesCartItem.article_shortname)
        : props.packagesCartItem.packageItem?.name
          ? translate(props.packagesCartItem.packageItem.name)
          : `${t('Item')} #${props.packagesCartItem.package_item_id}`

    const itemTypeLabel = props.packagesCartItem.packageItem?.type
        ? props.packagesCartItem.packageItem.type === PackageItemItemType.Upsell
            ? t('Upsell')
            : t('Access ticket')
        : t('Unknown')
    return (
        <>
            <UiTableRow className="group/row transition-colors hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                {/* 1. Item */}
                <UiTableCell className="py-3">
                    <div className={clsx('flex items-center gap-x-2')}>
                        <button
                            type={'button'}
                            aria-expanded={isOpen}
                            onClick={() => setIsOpen(!isOpen)}
                            className={clsx(
                                'flex items-center justify-center size-6 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
                            )}
                        >
                            <ChevronDownIcon
                                className={clsx(
                                    'size-4 transition-transform text-gray-400 group-hover/row:text-gray-600',
                                    isOpen && 'rotate-180 text-blue-500'
                                )}
                            />
                        </button>
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                                    {itemName}
                            </span>
                            {props.packagesCartItem.packageItem?.person_type === PackageItemPersonType.Child && (
                                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                                    {t('children')}
                                </span>
                            )}
                        </div>
                    </div>
                </UiTableCell>

                {/* 2. Quantity */}
                <UiTableCell className="py-3">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">
                        {quantity}x
                    </span>
                </UiTableCell>

                {/* 3. Actions (Conditional) */}
                {!props.isAccessTicket && (
                    <UiTableCell className="py-3">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                dispatch(
                                    openSecondaryModal(
                                        <UiAlertConfirmationModal
                                            title={t('Delete Item')}
                                            message={t(
                                                'Are you sure you want to delete this item?'
                                            )}
                                            onConfirm={async () => {
                                                try {
                                                    await dispatch(
                                                        SellerAction.fetchDeleteUpsell(
                                                            props.reservation.id,
                                                            props.packagesCartItem
                                                        )
                                                    )
                                                    toast.success(
                                                        t('Item deleted successfully')
                                                    )
                                                    dispatch(
                                                        BookingLoginAction.fetchSellerReservationDetail(
                                                            props.reservation.id
                                                        )
                                                    )
                                                    dispatch(closeSecondaryModal())
                                                } catch {
                                                    toast.error(t('Failed to delete item'))
                                                    dispatch(closeSecondaryModal())
                                                }
                                            }}
                                            onCancel={() => dispatch(closeSecondaryModal())}
                                        />,
                                        { modalSize: ModalSize.Small },
                                        t('Confirm')
                                    )
                                )
                            }}
                            className="p-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors"
                            title={t('Delete item') as string}
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </UiTableCell>
                )}

                {/* 4. Unit Price */}
                <UiTableCell className="py-3">
                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                        {props.packagesCartItem.packageItem?.original_price
                            ? currencyNumberFormat(props.packagesCartItem.packageItem.original_price)
                            : currencyNumberFormat(basePrice)}
                    </span>
                </UiTableCell>

                {/* 5. Unit Price with Discount */}
                <UiTableCell className="py-3">
                    <span className="text-gray-900 dark:text-gray-100 font-medium">
                        {currencyNumberFormat(basePrice)}
                    </span>
                </UiTableCell>

                {/* 6. Discount */}
                <UiTableCell className="py-3">
                    {discount > 1 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            -{discount.toFixed(0)}%
                        </span>
                    ) : (
                        <span className="text-gray-300">---</span>
                    )}
                </UiTableCell>

                {/* 7. Price */}
                <UiTableCell className="py-3" align="right">
                    <span className="text-gray-900 dark:text-gray-100 font-bold">
                        {currencyNumberFormat(props.packagesCartItem.price)}
                    </span>
                </UiTableCell>
            </UiTableRow>

            {isOpen && (
                <UiTableRow className={"group/row transition-colors  animate-in fade-in slide-in-from-top-1 "}>
                    <UiTableCell align="left" colSpan={7}>
                        <div className="duration-200 text-sm whitespace-pre-wrap space-y-1">
                            {props.packagesCartItem.custom_text || ''}
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {t('Type')}: {itemTypeLabel}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {t('Item ID')}: {props.packagesCartItem.package_item_id}
                            </div>
                        </div>
                    </UiTableCell>
                </UiTableRow>
            )}
        </>
    )
}

export default SellerPackageCartItemRow
